
import {throwError as observableThrowError, of as observableOf, merge as observableMerge,  Observable } from 'rxjs';

import {map, every, catchError, first, switchMap, tap} from 'rxjs/operators';
import { EcaSecurityAuthenticationService } from './../security-authentication/security-authentication.service';
import { EcaSecurityAuthorizationService } from './../security-authorization/security-authorization.service';
import { Injectable, inject } from '@angular/core';
import { EcaSecurityUsersService } from '../security-users/security-users.service';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class AppPermissionsService {
  private _ecaSecurityAuthenticationService = inject(EcaSecurityAuthenticationService);
  private _ecaSecurityAuthorizationService = inject(EcaSecurityAuthorizationService);
  private _ecaSecurityUsersService = inject(EcaSecurityUsersService);


  _userCache = {};
  fail: boolean;

  _allObservableTrue(...checks: Observable<boolean>[]): Observable<boolean> {
    return observableMerge(...checks).pipe(
      every((isAllowed) => !!isAllowed === true),
      catchError(() => {
        return observableOf(false);
      }), );
  }

  _someObservableTrue(...checks: Observable<boolean>[]): Observable<boolean> {
    const caughtChecks = checks.map((check) => check.pipe(catchError(() => observableOf(false))));
    return observableMerge(...caughtChecks).pipe(
      first((isAllowed) => !!isAllowed === true),
      catchError(() => {
        return observableOf(false);
      }), );
  }

  controlTruthyObservable(truthyObservable): Observable<boolean> {
    return truthyObservable.pipe(
      switchMap((value) => {
        if (value) {
          return observableOf(value);
        } else {
          return observableThrowError(value);
        }
      }));
  }

  getUser(userId: string): Observable<any> {
    return this._ecaSecurityAuthenticationService.getUser().pipe(
      switchMap((user) => {
        const cacheKey = `${user['userId']}_${userId}`;
        const cacheItem = this._userCache[cacheKey];

        if (cacheItem && cacheItem.validUntil > Date.now()) {
          return observableOf(cacheItem.data);
        }

        return this._ecaSecurityUsersService.get(userId).pipe(
          tap((_user) => {
            this._userCache[cacheKey] = {
              data: _user,
              validUntil: Date.now() + (5 * 60 * 60 * 1000)
            };
          }));
      }));
  }
  //  Permission Observable Checks

  hasRoutedUsersGroupResourcePermission(userId, resource): Observable<boolean> {
    return this.getUser(userId).pipe(
      switchMap((user) => {
        const groupIds = _.map(user.groups, 'groupId');
        const groupPermissions = _.map(groupIds, (groupId: string) => {
          const acl = `app:eca-security-service:groups:${resource}:${groupId}`;
          return this._ecaSecurityAuthorizationService.checkPermission(acl);
        });
        return this._someObservableTrue(...groupPermissions);
      }),
      map((value) => !!value),
      catchError(() => observableOf(false)), );
  }

  isAdministrator(): Observable<boolean> {
    return this._ecaSecurityAuthorizationService.checkPermission('app:eca-security-service:api:admin');
  }

  canViewApplication(): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission('DRCPORTAL:eca-security-ui:view')
    );
  }

  canViewGroup(groupId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:groups:view:${groupId}`)
    );
  }

  canEditGroup(groupId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:groups:edit:${groupId}`)
    );
  }

  canDeleteGroup(groupId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:groups:delete:${groupId}`)
    );
  }

  canViewUser(userId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:users:view:${userId}`),
      this.hasRoutedUsersGroupResourcePermission(userId, 'view')
    );
  }

  canEditUser(userId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:users:edit:${userId}`),
      this.hasRoutedUsersGroupResourcePermission(userId, 'edit')
    );
  }

  canDeactivateUser(userId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this.canEditUser(userId),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:users:deactivate:${userId}`),
      this.hasRoutedUsersGroupResourcePermission(userId, 'deactivate')
    );
  }

  canExpireUserPassword(userId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this.canEditUser(userId),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:users:expirePassword:${userId}`),
      this.hasRoutedUsersGroupResourcePermission(userId, 'expirePassword')
    );
  }

  canChangeUserPassword(userId: string): Observable<boolean> {
    return this._allObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:users:changePassword:${userId}`)
    );
  }

  canDeleteUser(userId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.isAdministrator(),
      this._ecaSecurityAuthorizationService.checkPermission(`app:eca-security-service:users:delete:${userId}`),
      this.hasRoutedUsersGroupResourcePermission(userId, 'delete')
    );
  }

  showEditUserActions(userId: string): Observable<boolean> {
    return this._someObservableTrue(
      this.canEditUser(userId),
      this.canDeactivateUser(userId),
      this.canExpireUserPassword(userId)
    );
  }
}
